import { Controller } from "@hotwired/stimulus"

// The last letter is fully shown just before the bottom of the page, once the reader has read the story.
const LINE_COMPLETE_AT = 0.98
// Number of letters ahead of the written part that are already faintly visible.
const FADE_LETTERS = 3
// On phones the CTA replaces the line, after the reader has had time to read it whole.
const CTA_DELAY_MS = 1500

// Hidden while the masthead name is on screen; slides in once it scrolls out above the viewport.
// The line writes itself letter by letter with the reader's scroll progress. Letters never go away once shown.
export default class extends Controller {
  static targets = ["line"]

  connect() {
    const anchor = document.querySelector("[data-reveal-bar-anchor]")
    if (anchor) {
      this.observer = new IntersectionObserver(([entry]) => {
        const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0
        this.element.classList.toggle("is-shown", scrolledPast)
      })
      this.observer.observe(anchor)
    }

    this.splitLetters()
    this.position = 0
    this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)")
    window.addEventListener("scroll", this.scheduleReveal, { passive: true })
    window.addEventListener("resize", this.scheduleReveal)
    this.revealLetters()
  }

  disconnect() {
    this.observer?.disconnect()
    window.removeEventListener("scroll", this.scheduleReveal)
    window.removeEventListener("resize", this.scheduleReveal)
    cancelAnimationFrame(this.frame)
    clearTimeout(this.ctaTimer)
  }

  scheduleReveal = () => {
    if (this.frame) return
    this.frame = requestAnimationFrame(() => {
      this.frame = null
      this.revealLetters()
    })
  }

  splitLetters() {
    const text = this.lineTarget.textContent.trim()
    this.letterCount = text.length
    this.lineTarget.style.setProperty("--fade", FADE_LETTERS)
    this.lineTarget.replaceChildren(...[...text].map((letter, index) => {
      const span = document.createElement("span")
      span.textContent = letter
      span.style.setProperty("--i", index)
      return span
    }))
  }

  // Position runs from 0 to letterCount + FADE_LETTERS; CSS turns it into each letter's opacity.
  revealLetters() {
    const scrollable = document.documentElement.scrollHeight - window.innerHeight
    const progress = scrollable > 0 ? window.scrollY / scrollable : 1
    const complete = progress >= LINE_COMPLETE_AT
    const end = this.letterCount + FADE_LETTERS
    const target = this.reducedMotion.matches ? (complete ? end : 0) : Math.min(progress / LINE_COMPLETE_AT, 1) * end

    if (target > this.position) {
      this.position = target
      this.lineTarget.style.setProperty("--pos", this.position)
    }

    if (complete && !this.ctaTimer) {
      this.ctaTimer = setTimeout(() => this.element.classList.add("is-complete"), CTA_DELAY_MS)
    }
  }
}
